import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import { PlusCircle } from 'lucide-react';

import { Subtype } from '../../types/ServiceAccordion.type';
import {
  CourseLevel,
  SuccessResponse,
} from '../../types/CourseStageAccordion.type';
import { apiPut } from '../../utils/apiClient';
import { notifyAPIClient, notifyFailure } from '../../utils/toast';
import CourseLevelCard from './CourseLevelCard';
import CourseLevelDialog from '../FormDialog/CourseLevelDialog';

interface CourseLevelListProps {
  courseType: Subtype;
  levels: CourseLevel[];
  fetchCourses: () => void;
}

const CourseLevelList: React.FC<CourseLevelListProps> = ({
  courseType,
  levels,
  fetchCourses,
}) => {
  const [openLevelDialog, setOpenLevelDialog] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [selectedLevel, setSelectedLevel] = useState<CourseLevel | null>(null);

  const handleCloseLevelDialog = () => {
    setOpenLevelDialog(false);
    setSelectedLevel(null);
  };

  const handleConfirmDeleteCourseLevel = async (force: boolean) => {
    try {
      const res = await apiPut<
        SuccessResponse,
        {
          subtypeId: number;
          level: number;
        }
      >(`/course/level/delete?force=${force}`, {
        subtypeId: Number(courseType.id),
        level: selectedLevel?.level || 0,
      });
      notifyAPIClient(res.message);
      setOpenDeleteDialog(false);
      setSelectedLevel(null);
      fetchCourses();
    } catch (error) {
      console.error('Error deleting subtype:', error);
      notifyFailure();
    }
  };

  return (
    <Box>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 2,
        }}
      >
        <Typography variant="body1" component="h3" fontWeight="bold">
          {courseType.name} Levels
        </Typography>

        <Button
          size="medium"
          variant="contained"
          color="primary"
          startIcon={<PlusCircle size={16} />}
          sx={{
            color: 'white',
          }}
          onClick={() => {
            setSelectedLevel(null);
            setOpenLevelDialog(true);
          }}
        >
          Add Level
        </Button>
      </Box>

      {levels.length === 0 ? (
        <Typography
          color="text.secondary"
          variant="body2"
          sx={{ fontStyle: 'italic', py: 2 }}
        >
          No level available
        </Typography>
      ) : (
        levels.map((level) => (
          <CourseLevelCard
            key={level.level}
            courseType={courseType}
            level={level}
            onEdit={(level) => {
              setSelectedLevel(level);
              setOpenLevelDialog(true);
            }}
            onDelete={(level) => {
              setSelectedLevel(level);
              setOpenDeleteDialog(true);
            }}
            fetchCourses={fetchCourses}
          />
        ))
      )}

      <CourseLevelDialog
        open={openLevelDialog}
        handleClose={handleCloseLevelDialog}
        courseLevel={selectedLevel}
        subtypeId={courseType.id}
        fetchCourses={fetchCourses}
      />

      {/* Delete dialog */}
      <Dialog
        open={openDeleteDialog}
        onClose={() => setOpenDeleteDialog(false)}
      >
        <DialogTitle>Delete Level</DialogTitle>
        <DialogContent>
          Do you want to delete this level? This action cannot be undone.
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setOpenDeleteDialog(false)}
            variant="contained"
            color="error"
          >
            Close
          </Button>
          <Button
            onClick={() => handleConfirmDeleteCourseLevel(false)}
            variant="contained"
            color="primary"
            className="!text-white"
          >
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CourseLevelList;
